import { z } from "zod";
import { canonicalSha256 } from "./canonicalize.js";
import { protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, UNIVERSAL_VERIFICATION_PROTOCOL, } from "./identifiers.js";
import { projectCompletionAssurance, } from "./verdict.js";
export const assuranceProjectionSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    lifecycleState: z.enum([
        "discovered",
        "compiled",
        "check_completed",
        "receipt_issued",
        "ineligible",
    ]),
    assuranceClass: z.enum(["none", "RETROSPECTIVE", "PROSPECTIVE"]),
    authority: z.enum(["local_check", "compiler", "canonical_engine", "human_oracle"]),
    scope: z.enum([
        "candidate",
        "check",
        "fixture_transport",
        "synthetic_canary",
        "internal_dogfood",
        "real_external_workflow",
    ]),
    mode: z.enum(["retrospective", "passive", "cooperative", "fully_mediated"]),
    signedReceiptExists: z.boolean(),
    currentReceiptEligible: z.boolean(),
    displayLabel: z.string().trim().min(1),
    blockedReasons: z.array(protocolIdSchema),
    projectedAt: protocolTimestampSchema,
    projectionDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.displayLabel.includes("verified") && !value.currentReceiptEligible) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["displayLabel"],
            message: "copy cannot map candidate/check success to verified completion",
        });
    }
    if (value.currentReceiptEligible && !value.signedReceiptExists) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["currentReceiptEligible"],
            message: "current verified completion requires a signed receipt",
        });
    }
});
function projectionDigest(input, sha256) {
    return canonicalSha256(input, sha256);
}
/**
 * Copy for a signed receipt that exists but cannot be shown as verified.
 *
 * Every branch must avoid the substring "verified" — `assuranceProjectionSchema`
 * refuses any label containing it while `currentReceiptEligible` is false, so
 * the honest-sounding "not verified" would be rejected by the very rail that
 * keeps the word truthful. Name the state instead of negating the claim.
 */
function unverifiedReceiptLabel(completion) {
    switch (completion.status) {
        // An affirmative violation must not read as ignorance. Before MISMATCH
        // existed the `default` below caught every unlisted status and printed
        // "unknown"; routing an accusation into that word is the exact conflation
        // §5.4 forbids, and it is silent — nothing fails, the reader is just
        // told the wrong thing. Named first so it cannot drift back into the tail.
        case "MISMATCH":
            return "evidence contradicts the claim";
        case "FAIL":
            return "failed verification";
        case "NOT_RUN":
            return "not run";
        case "PASS":
            // A pass that is blocked by freshness, or by retrospective assurance.
            return completion.assurance === "RETROSPECTIVE" ? "retrospective record" : "receipt issued";
        case "UNKNOWN":
            return "unknown";
        // "Not yet", named rather than negated — and deliberately NOT sharing
        // UNKNOWN's word. A reader who is told "unknown" goes looking for what
        // broke; a reader told the check is not yet due does not. The label must
        // also avoid the substring "verified" (see above), which is why this says
        // what the state IS rather than what it is not.
        case "PENDING":
            return "not yet due";
        // `UNSUPPORTED` is not a VerificationStatus, so this tail is still
        // reachable and cannot become `assertExhaustiveStatus`. It is now the ONLY
        // value that reaches it — every verification status above is named, so a
        // future status is a type error at `case "UNSUPPORTED"`, not a silent
        // "unknown".
        case "UNSUPPORTED":
            return "unsupported";
    }
}
export function projectAssurance(input) {
    const blockedReasons = new Set();
    const receipt = input.signedReceipt ?? null;
    // FACT ONE — freshness. Does the evidence this receipt was issued over still
    // describe the tree and environment in front of us? Owned here, and owned
    // here alone, which is why no clock is handed to the completion rule below.
    if (!receipt)
        blockedReasons.add("signed-receipt-missing");
    if (receipt && receipt.treeDigest !== input.currentTreeDigest)
        blockedReasons.add("receipt-tree-mismatch");
    if (receipt && receipt.environmentDigest !== input.currentEnvironmentDigest)
        blockedReasons.add("receipt-environment-mismatch");
    if (receipt &&
        Date.parse(input.projectedAt) - Date.parse(receipt.issuedAt) > input.maxReceiptAgeMs)
        blockedReasons.add("receipt-stale");
    const receiptEvidenceIsFresh = receipt !== null && blockedReasons.size === 0;
    // FACT TWO — the verdict. Did the checks actually pass, under prospective
    // assurance, with nothing else disqualifying? This is a different question
    // from freshness and it gets a different answer, so it is asked separately
    // and reported separately. `projectCompletionAssurance` is the canonical
    // rule; re-deriving it here would let the two drift apart.
    const completion = receipt ? projectCompletionAssurance({ receipt: receipt.receipt }) : null;
    if (completion && !completion.canDisplayVerified)
        for (const reasonCode of completion.reasonCodes)
            blockedReasons.add(reasonCode);
    const verdictSupportsVerified = completion?.canDisplayVerified === true;
    // Both facts must hold. A PASS over a stale tree and a FAIL over a fresh tree
    // are both ineligible, and `blockedReasons` says which one you are looking at.
    const currentReceiptEligible = receiptEvidenceIsFresh && verdictSupportsVerified;
    const lifecycleState = currentReceiptEligible
        ? "receipt_issued"
        : receipt
            ? "ineligible"
            : input.lifecycleState;
    const displayLabel = currentReceiptEligible
        ? "verified completion"
        : completion
            ? unverifiedReceiptLabel(completion)
            : input.lifecycleState === "check_completed"
                ? "check completed"
                : input.lifecycleState.replaceAll("_", " ");
    const withoutDigest = {
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        lifecycleState,
        assuranceClass: currentReceiptEligible ? receipt.assuranceClass : "none",
        authority: currentReceiptEligible ? "canonical_engine" : input.authority,
        scope: input.scope,
        mode: input.mode,
        signedReceiptExists: receipt !== null,
        currentReceiptEligible,
        displayLabel,
        blockedReasons: [...blockedReasons].sort(),
        projectedAt: input.projectedAt,
    };
    return assuranceProjectionSchema.parse({
        ...withoutDigest,
        projectionDigest: projectionDigest(withoutDigest, input.sha256),
    });
}
